import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { breadcrumbSlot } from "@redotech/redo-web/breadcrumb";
import { matchPath, Navigate } from "react-router-dom";
import { CartDrawer } from "../../../redo-supplies/cart-drawer";
import { RedoSuppliesCartProvider } from "../../../redo-supplies/redo-supplies-cart-context";

export const orderDetailBreadcrumb = breadcrumbSlot();

function hidePadding(url: string): boolean {
  return (
    !!matchPath("/stores/:storeId/redo-supplies/shop", url) ||
    !!matchPath(
      "/stores/:storeId/redo-supplies/product/:productFamilyId",
      url,
    ) ||
    !!matchPath(
      "/stores/:storeId/redo-supplies/shop/checkout/:checkoutSessionId",
      url,
    ) ||
    !!matchPath("/stores/:storeId/redo-supplies/order-history", url) ||
    !!matchPath("/stores/:storeId/redo-supplies/order-history/:orderId", url) ||
    !!matchPath("/stores/:storeId/redo-supplies/recurring-purchases/*", url)
  );
}

export const redoSuppliesRoutes: PermissionedRouteObject[] = [
  {
    handle: { breadcrumb: "Supplies" },
    async lazy() {
      const { Page } = await import("../../page");
      return {
        element: (
          <RedoSuppliesCartProvider>
            <Page hidePadding={hidePadding} />
            <CartDrawer />
          </RedoSuppliesCartProvider>
        ),
      };
    },
    children: [
      { index: true, element: <Navigate replace to="shop" /> },
      {
        path: "shop",
        handle: { breadcrumb: "Shop" },
        async lazy() {
          const { RedoSuppliesShopPage } = await import(
            "../../../redo-supplies/shop-page"
          );
          return { Component: RedoSuppliesShopPage };
        },
      },
      {
        path: "product/:productFamilyId",
        handle: { breadcrumb: "Product" },
        async lazy() {
          const { RedoSuppliesProductDetailPage } = await import(
            "../../../redo-supplies/product-detail-page"
          );
          return { Component: RedoSuppliesProductDetailPage };
        },
      },
      {
        path: "shop/checkout/:checkoutSessionId",
        handle: { breadcrumb: "Checkout" },
        async lazy() {
          const { RedoSuppliesCheckoutPage } = await import(
            "../../../redo-supplies/checkout-page"
          );
          return { Component: RedoSuppliesCheckoutPage };
        },
      },
      {
        path: "order-history",
        handle: { breadcrumb: "Order history" },
        children: [
          {
            index: true,
            async lazy() {
              const { RedoSuppliesOrderHistoryPage } = await import(
                "../../../redo-supplies/order-history-page"
              );
              return { Component: RedoSuppliesOrderHistoryPage };
            },
          },
          {
            path: ":orderId",
            handle: { breadcrumb: orderDetailBreadcrumb },
            async lazy() {
              const { RedoSuppliesOrderDetailPage } = await import(
                "../../../redo-supplies/order-detail-page"
              );
              return {
                element: (
                  <RedoSuppliesOrderDetailPage
                    breadcrumb={orderDetailBreadcrumb}
                  />
                ),
              };
            },
          },
        ],
      },
      {
        path: "recurring-purchases",
        handle: { breadcrumb: "Recurring purchases" },
        children: [
          { index: true, element: <Navigate replace to="inventory" /> },
          {
            path: "inventory",
            handle: { breadcrumb: "Inventory-based" },
            async lazy() {
              const { RedoSuppliesRecurringPurchasesPage } = await import(
                "../../../redo-supplies/recurring-purchases-page"
              );
              return { Component: RedoSuppliesRecurringPurchasesPage };
            },
          },
          {
            path: "time",
            handle: { breadcrumb: "Time-based" },
            async lazy() {
              const { RedoSuppliesTimeBasedPage } = await import(
                "../../../redo-supplies/time-based-page"
              );
              return { Component: RedoSuppliesTimeBasedPage };
            },
          },
        ],
      },
    ],
  },
];
